import { Button } from '@dexma/ui-components';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { defaultDateFormatter } from 'adapter/in/common/utils/widgetDataUtils';
import { PAGES_PER_BATCH } from 'adapter/in/common/widget/widgetHooks';
import { A4_ROWS_PER_PAGE } from 'adapter/in/dashboards/components/Grid';
import { mapLayoutWidgetsToGridElements } from 'adapter/in/dashboards/components/Grid/gridUtils';
import GeneratePDFOverlayContainer from 'adapter/in/reports/containers/GeneratePDFOverlayContainer';
import { generateVectorizedPDF } from 'adapter/in/reports/utils/generatePDF';
import Layout, { LayoutElementType, LayoutType } from 'domain/entity/layout';
import { ReportInstance } from 'domain/entity/reportInstance';
import { isNone, Option } from 'fp-ts/lib/Option';
import React, { createContext, ReactNode, RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type GeneratePDFContext = {
    elements: number;
    loadedElements: number;
    generatingPDF: boolean;
    triggerWidgetLoads: boolean;
    reportRef: RefObject<HTMLDivElement>;
    currentPageBatch: number;
};

type GeneratePDFStateDispatch = {
    registerLoader: () => () => void;
    generatePDF: (reportInstance: ReportInstance) => void;
};

export type GeneratePDFProviderProps = {
    children: ReactNode;
    layoutData: Option<Layout[]>;
    timeoutDelay?: number;
};

export const getFormattedPeriod = (report: ReportInstance, profileDateFormat: string): string =>
    `${defaultDateFormatter(report.context.periodFrom, profileDateFormat.toUpperCase())} - ${defaultDateFormatter(
        report.context.periodTo,
        profileDateFormat.toUpperCase()
    )}`;

export const getPDFName = (report: ReportInstance, profileDateFormat: string): string => {
    const period = getFormattedPeriod(report, profileDateFormat);
    const reportName = report.templateDashboardName || report.name;
    const PDFName = `${reportName}_${report.context.locationName}_${period.replaceAll(' - ', '_').replaceAll('/', '-')}_${
        report.context.languageName
    }`;
    return PDFName.replaceAll(' ', '');
};

export const GeneratePDFStateDispatchContext = createContext({} as GeneratePDFStateDispatch);
const ProgressStateContext = createContext({} as GeneratePDFContext);

const dispatchGeneratePDF = (): GeneratePDFStateDispatch => useContext(GeneratePDFStateDispatchContext);
const useGeneratePDF = (): GeneratePDFContext => useContext(ProgressStateContext);

const GeneratePDFContextProvider = (props: GeneratePDFProviderProps) => {
    const { children, layoutData, timeoutDelay } = props;
    const [elements, setElements] = useState(0);
    const [loadedElements, setLoadedElements] = useState(0);
    const [triggerWidgetLoads, setTriggerWidgetLoads] = useState(false);
    const reportRef = useRef<HTMLDivElement>(null);
    const [generatingPDF, setGeneratingPDF] = useState<boolean>(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
    const [report, setReport] = useState<ReportInstance>();
    const [PDFName, setPDFName] = useState<string>('');
    const [simulatedProgress, setSimulatedProgress] = useState(50);
    const [pdfCanGenerate, setPdfCanGenerate] = useState(false);
    const [currentPageBatch, setCurrentPageBatch] = useState<number>(0);
    const { profile } = useProfile();

    const totalPages = useMemo(() => {
        if (isNone(layoutData)) return 1;

        const layouts = layoutData.value;
        const a4Layout = layouts.find((l) => l.type === LayoutType.A4);

        if (!a4Layout) return 1;

        const gridElements = mapLayoutWidgetsToGridElements(a4Layout, false);

        const userWidgets = gridElements.filter((w) => w.type !== LayoutElementType.A4_HEADER && w.type !== LayoutElementType.A4_FOOTER);

        const maxRow = Math.max(0, ...userWidgets.map((w) => w.y + w.h));

        return Math.ceil(maxRow / A4_ROWS_PER_PAGE);
    }, [layoutData]);

    const widgetsPerPage = useMemo(() => {
        if (isNone(layoutData)) return [];

        const layouts = layoutData.value;
        const a4Layout = layouts.find((l) => l.type === LayoutType.A4);
        if (!a4Layout) return [];

        const gridElements = mapLayoutWidgetsToGridElements(a4Layout, false);

        return gridElements.filter((w) => w.type !== LayoutElementType.A4_HEADER && w.type !== LayoutElementType.A4_FOOTER);
    }, [layoutData]);

    const waitForPaint = async () => {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    };

    useEffect(() => {
        if (
            loadedElements === elements &&
            report &&
            PDFName &&
            reportRef.current &&
            generatingPDF &&
            pdfPreviewUrl === null &&
            pdfCanGenerate
        ) {
            // Small delay to ensure all rendering is complete.
            // This solution is not optimal, but the alternative would require lots of
            // changes in the way widgets communicate, which is tricky.
            // TODO: We should replace this by paginated queries to the backend to fetch
            // the data in smaller chunks, and only when needed.
            waitForPaint().then(() => {
                generateVectorizedPDF(reportRef.current!, report, layoutData, PDFName)
                    .then((pdfUrl) => {
                        setPdfPreviewUrl(pdfUrl);
                        setGeneratingPDF(false);
                    })
                    .catch((error) => {
                        console.error('Error generating PDF:', error);
                        setGeneratingPDF(false);
                    });
            });
        }
    }, [loadedElements, elements, report, generatingPDF, pdfPreviewUrl, pdfCanGenerate, PDFName]);

    useEffect(() => {
        if (report) {
            const name = getPDFName(report, profile.dateFormat);
            setPDFName(name);
        }
    }, [report, profile.dateFormat]);

    useEffect(
        () => () => {
            if (pdfPreviewUrl) {
                URL.revokeObjectURL(pdfPreviewUrl);
            }
        },
        [pdfPreviewUrl]
    );

    useEffect(() => {
        if (generatingPDF && loadedElements === elements) {
            const interval = setInterval(() => {
                setSimulatedProgress((prev) => {
                    if (prev < 90) {
                        return prev + 15;
                    }
                    return prev;
                });
            }, 500);

            return () => clearInterval(interval);
        }
        return undefined;
    }, [generatingPDF, loadedElements, elements]);

    useEffect(() => {
        if (!generatingPDF || !report) return;

        const maxBatch = Math.ceil(totalPages / PAGES_PER_BATCH);

        const batchLimitPage = (currentPageBatch + 1) * PAGES_PER_BATCH;

        const expectedWidgets = widgetsPerPage.filter((w) => Math.floor(w.y / A4_ROWS_PER_PAGE) < batchLimitPage).length;

        if (loadedElements >= expectedWidgets && currentPageBatch < maxBatch - 1) {
            setCurrentPageBatch((prev) => prev + 1);
        }
    }, [loadedElements, generatingPDF, report, currentPageBatch, totalPages, widgetsPerPage]);

    const registerLoader = () => {
        setElements((prev) => prev + 1);
        let deregistered = false;
        return () => {
            if (!deregistered) {
                deregistered = true;
                setLoadedElements((prev) => prev + 1);
            }
        };
    };

    const generatePDF = (reportInstance: ReportInstance) => {
        setTriggerWidgetLoads(true);
        setCurrentPageBatch(0);
        setPdfPreviewUrl(null);

        if (report?.id !== reportInstance.id) {
            setReport(reportInstance);
        }

        setSimulatedProgress(50);
        setGeneratingPDF(true);
    };

    const onDownload = () => {
        if (pdfPreviewUrl && report && PDFName) {
            const link = document.createElement('a');
            link.href = pdfPreviewUrl;
            link.download = `${PDFName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };
    const dispatchValue = useMemo(() => ({ registerLoader, generatePDF }), []);
    const stateValue = useMemo(() => ({ elements, loadedElements, triggerWidgetLoads, reportRef, generatingPDF, currentPageBatch }), [
        elements,
        loadedElements,
        triggerWidgetLoads,
        generatingPDF,
        currentPageBatch,
    ]);
    const isDataReady = loadedElements === elements && elements > 0;
    const isPdfReady = !!pdfPreviewUrl;

    let totalProgressPercent;
    if (isPdfReady) {
        totalProgressPercent = 100;
    } else if (isDataReady) {
        totalProgressPercent = simulatedProgress;
    } else {
        totalProgressPercent = (loadedElements / elements) * 50;
    }

    return (
        <ProgressStateContext.Provider value={stateValue}>
            <GeneratePDFStateDispatchContext.Provider value={dispatchValue}>
                {generatingPDF && report && (
                    <GeneratePDFOverlayContainer
                        reportInstance={report}
                        progress={totalProgressPercent}
                        onDownload={onDownload}
                        elements={elements}
                        loadedElements={loadedElements}
                        onReadyToGeneratePDF={(status: boolean) => {
                            setPdfCanGenerate(status);
                        }}
                    />
                )}
                {children}
                {pdfPreviewUrl && !generatingPDF && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                            padding: '20px',
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                width: '90%',
                                height: '90%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 20px',
                                    borderBottom: '1px solid #eee',
                                }}
                            >
                                <h3 style={{ margin: 0 }}>PDF Preview</h3>
                                <div>
                                    <Button
                                        onClick={() => {
                                            onDownload();
                                        }}
                                        style={{
                                            marginRight: '10px',
                                        }}
                                        text='Download'
                                    />
                                    <Button variant='outline' onClick={() => setPdfPreviewUrl(null)} text='Close' />
                                </div>
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                <iframe
                                    src={`${pdfPreviewUrl}#zoom=130%`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                    }}
                                    title='PDF Preview'
                                />
                            </div>
                        </div>
                    </div>
                )}
            </GeneratePDFStateDispatchContext.Provider>
        </ProgressStateContext.Provider>
    );
};

export { GeneratePDFContextProvider, dispatchGeneratePDF, useGeneratePDF };
